/**
 * Keeps the checked option of a scrollable option list in view. A filter is regularly activated
 * from outside its own panel — a deep link, a restored lookup, a search suggestion — and in a long
 * list (brands, brand holders) the option that ends up checked sits below the fold, leaving no
 * visible sign of what is narrowing the results.
 *
 * Only the list itself scrolls; scrollIntoView would drag every scrollable ancestor along with it
 * and yank the page towards the filter panel on load.
 */
export function checked_option_scroller() {
    let list: Element | null = null
    let observer: ResizeObserver | null = null
    let aligned_id: string | null = null

    /**
     * @param resized - Called from the size observer, where the list may have grown into a height
     *   it could not be measured against before, so the previous alignment has to be redone.
     */
    function align(resized = false) {
        const container = list
        // Cheap first: no layout is read until the checked option changed or the list resized.
        const checked = container?.querySelector<HTMLInputElement>('input:checked')
        if (!container || !checked) {
            // Clearing the filter re-arms the scroller, so picking the same option again moves.
            aligned_id = null
            return
        }
        if (!resized && checked.id === aligned_id) {
            return
        }
        // A collapsed body, a panel still opening, a list not laid out yet: nothing to scroll within,
        // and the observer calls back once that changes.
        const height = container.clientHeight
        if (!height) {
            return
        }
        const option = checked.closest('.item')
        if (!option) {
            return
        }

        aligned_id = checked.id
        const list_top = container.getBoundingClientRect().top
        const option_box = option.getBoundingClientRect()
        if (option_box.top >= list_top && option_box.bottom <= list_top + height) {
            // Already in view — the common case when the user just clicked it. Moving the list out
            // from under the pointer would be worse than doing nothing.
            return
        }
        // Park it at the top rather than at the nearest edge: the selection is what the user came to
        // see, and the options below it are the ones they are likely to switch to. Instant, because a
        // smooth scroll is still running while the collection settles, and the next DOM patch inside
        // the list cancels it halfway.
        container.scrollTo({behavior: 'instant', top: container.scrollTop + option_box.top - list_top})
    }

    return function scroll_checked_into_view(dom: Element) {
        if (dom !== list) {
            observer?.disconnect()
            list = dom
            aligned_id = null
            observer = new ResizeObserver(() => align(true))
            observer.observe(list)
        }
        align()
    }
}
